import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Robot, House, EnvelopeSimple, Users, ChatCircleDots, GearSix, SignOut, Lightning, ChartLineUp, BookOpen } from "@phosphor-icons/react";

export default function DashboardLayout({ children, title, actions }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const items = [
    { to: "/dashboard", icon: House, label: "Overview" },
    { to: "/dashboard/email", icon: EnvelopeSimple, label: "Email" },
    { to: "/dashboard/leads", icon: Users, label: "Leads" },
    { to: "/dashboard/bot", icon: ChatCircleDots, label: "Response Bot" },
    { to: "/dashboard/knowledge", icon: BookOpen, label: "Knowledge" },
    { to: "/dashboard/analytics", icon: ChartLineUp, label: "Analytics" },
    { to: "/dashboard/settings", icon: GearSix, label: "Settings" },
  ];
  return (
    <div className="min-h-screen flex bg-[var(--bg-soft)]">
      <aside className="w-[260px] shrink-0 bg-[var(--bg-dark)] text-white flex flex-col" data-testid="dashboard-sidebar">
        <Link to="/" className="px-6 py-6 border-b border-white/10 flex items-center gap-2">
          <div className="w-8 h-8 bg-[var(--accent)] flex items-center justify-center">
            <Robot size={20} weight="duotone" color="#0A0A0A" />
          </div>
          <span className="font-display text-xl tracking-tight">Robocope</span>
        </Link>
        <nav className="flex-1 py-6 px-3 space-y-1">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/dashboard"}
              data-testid={`nav-${it.label.toLowerCase().replace(/\s/g, '-')}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                  isActive ? "bg-[var(--brand)] text-white" : "text-white/70 hover:bg-white/5 hover:text-white"
                }`
              }
            >
              <it.icon size={18} weight="duotone" />
              {it.label}
            </NavLink>
          ))}
        </nav>
        <div className="px-3 pb-3 border-t border-white/10 pt-4">
          <div className="px-4 py-3 mb-2">
            <div className="overline text-white/50">Signed in</div>
            <div className="font-medium text-sm truncate" data-testid="sidebar-user-email">{user?.email}</div>
          </div>
          <button
            onClick={async () => { await logout(); nav("/"); }}
            data-testid="logout-btn"
            className="w-full flex items-center gap-3 px-4 py-3 text-sm font-medium text-white/70 hover:bg-white/5 hover:text-white transition-colors"
          >
            <SignOut size={18} weight="duotone" />
            Log out
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        <header className="bg-white border-b border-[var(--line)] px-10 py-6 flex items-center justify-between">
          <div>
            <div className="overline text-[var(--ink-soft)]">Robocope · AI Workforce</div>
            <h1 className="text-3xl font-display tracking-tight mt-1" data-testid="page-title">{title}</h1>
          </div>
          <div className="flex items-center gap-3">{actions}</div>
        </header>
        <div className="p-10">{children}</div>
      </main>
    </div>
  );
}

export function StatCard({ label, value, hint, accent }) {
  return (
    <div className={`cell-strong p-6 ${accent ? "bg-[var(--accent)]" : "bg-white"}`}>
      <div className="overline text-[var(--ink-soft)]">{label}</div>
      <div className="text-5xl font-display mt-2" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>{value}</div>
      {hint && <div className="text-sm text-[var(--ink-soft)] mt-2">{hint}</div>}
    </div>
  );
}
